import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ArrowUpRightIcon, type Icon} from '@primer/octicons-react'
import {Suspense, type ReactNode} from 'react'
import {MiniStatusSkeletonItem} from './MiniMergeStatusLoading'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import type {To} from '@github-ui/react-router'
import {isForYouRefreshEnabled} from '../../shared/for-you-feature-flags'

export type MiniStatusAction = (returnFocusTo?: HTMLElement | null) => void

type MiniMergeStatusBoundaryProps = {
  action?: MiniStatusAction
  to?: To
  actionLabel: string
  boundaryName: string
  children: ReactNode
  isFirst?: boolean
  text: string
  /** Icon shown on the trailing side of the loading skeleton. Defaults to ArrowUpRightIcon. */
  icon?: Icon
}

export function MiniMergeStatusBoundary({
  action,
  actionLabel,
  boundaryName,
  children,
  isFirst,
  to,
  text,
  icon,
}: MiniMergeStatusBoundaryProps) {
  return (
    <ErrorBoundary
      boundaryName={boundaryName}
      fallback={
        <MiniMergeStatusErrorItem action={action} actionLabel={actionLabel} isFirst={isFirst} text={text} to={to} />
      }
    >
      <Suspense fallback={<MiniStatusSkeletonItem text={text} action={action} icon={icon} isFirst={isFirst} />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

function MiniMergeStatusErrorItem({
  action,
  actionLabel,
  isFirst,
  text,
  to,
}: Omit<MiniMergeStatusBoundaryProps, 'boundaryName' | 'children' | 'icon'>) {
  const unavailableText = `${text} unavailable`
  const isRefreshEnabled = isForYouRefreshEnabled()

  return (
    <MiniMergeStatusItem
      aria-label={isRefreshEnabled && !action && !to ? unavailableText : actionLabel}
      isFirst={isFirst}
      onClick={
        isRefreshEnabled
          ? action
            ? event => action(event.currentTarget)
            : undefined
          : event => action?.(event.currentTarget)
      }
      to={to}
    >
      <MiniMergeStatusItem.LabelRow icon={isRefreshEnabled && !action && !to ? undefined : ArrowUpRightIcon}>
        <MiniMergeStatusItem.Label icon="error">{unavailableText}</MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      <MiniMergeStatusItem.Progress
        aria-label={unavailableText}
        isUnavailable={false}
        successful={0}
        total={0}
        unavailableLabel={unavailableText}
      />
    </MiniMergeStatusItem>
  )
}
